import React from 'react';
import PropTypes from 'prop-types';
import { Landmark, Check, X, Hourglass } from 'lucide-react';
import Locales from 'locales';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';

const CONST_PASSPORT = 'PASSPORT'; // The pilgrim should provide their passport
const CONST_HEALTH = 'HEALTH'; // The pilgrim should go through the health check
const CONST_HEALTH_FAIL = 'HEALTH_FAIL'; // The pilgrim failed health check
const CONST_VISA_SUBMISSION = 'VISA_SUBMISSION'; // The pilgrim should go through the visa delivery service
const CONST_VISA_IN_PROGRESS = 'VISA_IN_PROGRESS'; // The visa requested has been submitted to MAEC and pending response
const CONST_VISA_GRANT = 'VISA_GRANT'; // The pilgrim should go through the visa delivery service
const CONST_TRAVEL_TICKET = 'TRAVEL_TICKET'; // The pilgrim should get their travel ticket
const CONST_COMPLETED = 'COMPLETED'; // The pilgrim has gone through all process and it ready to travel.
const CONST_ABORTED = 'ABORTED'; // The trip has been aborted
const CONST_DEPOSIT_REGISTER = 'FEE_REGISTER'; // The pilgrim should pay the registration fee
const CONST_DEPOSIT_CART = 'FEE_CART'; // The pilgrim should now pay the total of his cart with selected options
const CONST_INVOICE_REGISTER = 'INVOICE_REGISTER'; // The pilgrim is waiting for the agency to pay registration invoice to bank
const CONST_INVOICE_CART = 'INVOICE_CART'; // The pilgrim is waiting for the agency to pay the balance invoice to the bank

export const MOMO_MAX_AMOUNT = 1950000;

export const CATEGORY_STANDARD = 'Standard';

export const CATEGORY_PREMIUM = 'Premium';

export const CATEGORY_NON_RESIDENT = 'Non résidents';

export const CATEGORY_NON_RESIDENT_STANDARD = 'Non résidents Standard';

export const CATEGORY_NON_RESIDENT_PREMIUM = 'Non résidents Premium';

export const CATEGORY_ENCADREUR_RELIGIEUX = 'Encadreur réligieux';

export const CATEGORY_ENCADREUR_MEDICAL = 'Encadreur médical';

export const CATEGORY_NON_RESIDENT_TOTAL_SEATS = 56;

export const defaultCategories = [
  CATEGORY_STANDARD,
  CATEGORY_PREMIUM,
  CATEGORY_NON_RESIDENT_STANDARD,
  CATEGORY_NON_RESIDENT_PREMIUM,
];

const specialCategories = [CATEGORY_ENCADREUR_RELIGIEUX, CATEGORY_ENCADREUR_MEDICAL];

export const agencyCategories = [...defaultCategories, ...specialCategories];

export const categoryBadgeVariant = (categoryName) => {
  let res = 'standard';

  if (categoryName === CATEGORY_STANDARD || categoryName === CATEGORY_PREMIUM) {
    res = categoryName.toLowerCase();
  } else if (categoryName === CATEGORY_NON_RESIDENT_STANDARD) {
    res = 'diaspora';
  } else if (categoryName === CATEGORY_NON_RESIDENT_PREMIUM) {
    res = 'diaspora_2';
  } else if (categoryName === CATEGORY_ENCADREUR_RELIGIEUX) {
    res = 'supervisor';
  } else if (categoryName === CATEGORY_ENCADREUR_MEDICAL) {
    res = 'supervisor_2';
  } else {
    res = 'other';
  }

  return res;
};

const statusIndicatorClasses = {
  default: {
    success: 'bg-green-400',
    partial: 'bg-yellow-400',
    error: 'bg-red-300',
    pending: 'bg-gray-300',
  },
  compact: {
    success: 'bg-[#e2f1e8] text-[#206c40]',
    partial: 'bg-[#fff1cf] text-[#896400]',
    error: 'bg-[#fff0f2] text-[#b13c47]',
    pending: 'bg-[#e9eeeb] text-[#596c60]',
  },
};

const StatusIndicator = ({
  icon: Icon,
  label,
  tone,
  appearance = 'default',
  portalled = false,
  tooltipClassName = '',
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        tabIndex={portalled ? 0 : undefined}
        aria-label={label}
        className={cn(
          'rounded-full',
          appearance === 'compact' ? 'inline-flex size-5 shrink-0 items-center justify-center p-0 leading-none' : 'p-1',
          statusIndicatorClasses[appearance][tone]
        )}
      >
        <Icon className="size-3" aria-hidden="true" />
      </span>
    </TooltipTrigger>
    <TooltipContent portalled={portalled} className={tooltipClassName}>
      <p>{label}</p>
    </TooltipContent>
  </Tooltip>
);

StatusIndicator.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(Object.keys(statusIndicatorClasses.default)).isRequired,
  appearance: PropTypes.oneOf(Object.keys(statusIndicatorClasses)),
  portalled: PropTypes.bool,
  tooltipClassName: PropTypes.string,
};

export const FullPaymentIcon = ({ appearance = 'default', portalled = false, tooltipClassName = '' }) => (
  <StatusIndicator
    icon={Landmark}
    label={Locales.trip.table.tooltips.participation_paid}
    tone="success"
    appearance={appearance}
    portalled={portalled}
    tooltipClassName={tooltipClassName}
  />
);

export const NpiCheckSuccessIcon = ({ appearance = 'default', portalled = false, tooltipClassName = '' }) => (
  <StatusIndicator
    icon={Check}
    label="NPI valide"
    tone="success"
    appearance={appearance}
    portalled={portalled}
    tooltipClassName={tooltipClassName}
  />
);

export const NpiCheckFailIcon = ({ appearance = 'default', portalled = false, tooltipClassName = '' }) => (
  <StatusIndicator
    icon={X}
    label="NPI non valide"
    tone="error"
    appearance={appearance}
    portalled={portalled}
    tooltipClassName={tooltipClassName}
  />
);

export const NpiCheckPendingIcon = ({ appearance = 'default', portalled = false, tooltipClassName = '' }) => (
  <StatusIndicator
    icon={Hourglass}
    label="Validation NPI en attente"
    tone="pending"
    appearance={appearance}
    portalled={portalled}
    tooltipClassName={tooltipClassName}
  />
);

export const PartialPaymentIcon = ({ appearance = 'default', portalled = false, tooltipClassName = '' }) => (
  <StatusIndicator
    icon={Landmark}
    label="Acompte payé"
    tone="partial"
    appearance={appearance}
    portalled={portalled}
    tooltipClassName={tooltipClassName}
  />
);

const statusIconPropTypes = {
  appearance: PropTypes.oneOf(Object.keys(statusIndicatorClasses)),
  portalled: PropTypes.bool,
  tooltipClassName: PropTypes.string,
};

FullPaymentIcon.propTypes = statusIconPropTypes;
PartialPaymentIcon.propTypes = statusIconPropTypes;
NpiCheckSuccessIcon.propTypes = statusIconPropTypes;
NpiCheckFailIcon.propTypes = statusIconPropTypes;
NpiCheckPendingIcon.propTypes = statusIconPropTypes;

export const nextStepsArray = [
  {
    tooltip: Locales.trip.next_steps.passport_required.tooltip,
    label: Locales.trip.next_steps.passport_required.label,
    value: CONST_PASSPORT,
    iconClassName: 'fa-regular fa-address-card',
  },
  {
    tooltip: Locales.trip.next_steps.passport_submitted.tooltip,
    label: Locales.trip.next_steps.passport_submitted.label,
    value: CONST_VISA_IN_PROGRESS,
    color: 'success',
    iconClassName: 'fa-regular fa-star-and-crescent',
  },
  {
    tooltip: Locales.trip.next_steps.passport_to_submit.tooltip,
    label: Locales.trip.next_steps.passport_to_submit.label,
    value: CONST_VISA_SUBMISSION,
    color: 'info',
    iconClassName: 'fa-regular fa-kaaba',
  },
  {
    tooltip: Locales.trip.next_steps.medical_check_required.tooltip,
    label: Locales.trip.next_steps.medical_check_required.label,
    value: CONST_HEALTH,
    color: 'success',
    iconClassName: 'fa-regular fa-stethoscope',
  },
  {
    tooltip: Locales.trip.next_steps.medical_check_failed.tooltip,
    label: Locales.trip.next_steps.medical_check_failed.label,
    value: CONST_HEALTH_FAIL,
    color: 'danger',
    iconClassName: 'fa-regular fa-stethoscope',
  },
  {
    tooltip: Locales.trip.next_steps.registration_fee.tooltip,
    label: Locales.trip.next_steps.registration_fee.label,
    value: CONST_DEPOSIT_REGISTER,
    color: '',
    iconClassName: 'fa-regular fa-sack-dollar',
  },
  {
    tooltip: Locales.trip.next_steps.visa_received.tooltip,
    label: Locales.trip.next_steps.visa_received.label,
    value: CONST_VISA_GRANT,
    color: 'success',
    iconClassName: 'fa-regular fa-star-and-crescent',
  },
  {
    tooltip: Locales.trip.next_steps.travel_ticket.tooltip,
    label: Locales.trip.next_steps.travel_ticket.label,
    value: CONST_TRAVEL_TICKET,
    iconClassName: 'fa-regular fa-address-card',
  },
  {
    tooltip: Locales.trip.next_steps.process_completed.tooltip,
    label: Locales.trip.next_steps.process_completed.label,
    value: CONST_COMPLETED,
    color: 'success',
    iconClassName: 'fa-regular fa-circle-check',
  },
  {
    tooltip: Locales.trip.next_steps.process_aborted.tooltip,
    label: Locales.trip.next_steps.process_aborted.label,
    value: CONST_ABORTED,
    color: 'danger',
    iconClassName: 'fa-regular fa-ban',
  },
  {
    tooltip: Locales.trip.next_steps.balance_deposit.tooltip,
    label: Locales.trip.next_steps.balance_deposit.label,
    value: CONST_DEPOSIT_CART,
    color: '',
    iconClassName: 'fa-regular fa-sack-dollar',
  },
  {
    tooltip: Locales.trip.next_steps.registration_transfer.tooltip,
    label: Locales.trip.next_steps.registration_transfer.label,
    color: 'danger',
    value: CONST_INVOICE_REGISTER,
    iconClassName: 'fa-regular fa-file-invoice-dollar',
  },
  {
    tooltip: Locales.trip.next_steps.balance_transfer.tooltip,
    label: Locales.trip.next_steps.balance_transfer.label,
    color: 'danger',
    value: CONST_INVOICE_CART,
    iconClassName: 'fa-regular fa-file-invoice-dollar',
  },
];

export const categoriesDetails = {
  [CATEGORY_STANDARD]: {
    name: CATEGORY_STANDARD,
    transport: 'Vol Spécial Hadj (Cabine Eco)',
    mecca: '4 lits / chambre',
    medina: '4 lits / chambre',
    service: 'Catégorie B - Zone 4',
    documents: [],
  },
  [CATEGORY_PREMIUM]: {
    name: CATEGORY_PREMIUM,
    transport: 'Vol Spécial Hadj (Cabine Business)',
    mecca: '2 lits / chambre',
    medina: '2 lits / chambre',
    service: 'Catégorie B - Zone 4 \n Tente séparée avec buffet',
    documents: [],
  },
  [CATEGORY_NON_RESIDENT_STANDARD]: {
    name: CATEGORY_NON_RESIDENT_STANDARD,
    transport: 'Vol commercial',
    mecca: '4 lits / chambre',
    medina: '4 lits / chambre',
    service: 'Catégorie B - Zone 4',
    documents: [
      "Copie du billet d'avion portant la date d'arrivée fixée par AGLO",
      "3 photos d'identité à fond blanc",
      "Attestation d'immatriculation consulaire",
    ],
  },
  [CATEGORY_NON_RESIDENT_PREMIUM]: {
    name: CATEGORY_NON_RESIDENT_PREMIUM,
    transport: 'Vol commercial',
    mecca: '2 lits / chambre',
    medina: '2 lits / chambre',
    service: 'Catégorie B - Zone 4 \n Tente séparée avec buffet',
    documents: [
      "Copie du billet d'avion portant la date d'arrivée fixée par AGLO",
      "3 photos d'identité à fond blanc",
      "Attestation d'immatriculation consulaire",
    ],
  },
};

export const DEPARTEMENTS = [
  'ALIBORI',
  'ATACORA',
  'ATLANTIQUE',
  'BORGOU',
  'COLLINES',
  'COUFFO',
  'DONGA',
  'LITTORAL',
  'MONO',
  'OUÉMÉ',
  'PLATEAU',
  'ZOU',
];
